import React from 'react'
import { StrictMode, useEffect } from 'react'
import 'core-js/proposals/promise-with-resolvers'
import { ExtraErrorData } from '@sentry/integrations'
import { init, reactRouterV6Instrumentation, setTag, withProfiler } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { pdfjs } from 'react-pdf'

// Function to detect if we need to use legacy build
function shouldUseLegacyBuild() {
  try {
    if (typeof window === 'undefined') return false

    console.log(isSafari ? 'Running on Safari' : 'Not running on Safari')
    if (isSafari) {
      // Extract Safari version - matches "Version/18" format
      const match = ua.match(/Version\/(\d+)/i)
      console.log('Safari version:', match)
      if (!match || !match[1]) return true // If we can't determine version, use legacy to be safe

      const version = parseInt(match[1])
      return version < 18 // Use legacy build for Safari versions equal or below 18
    }
    return false
  } catch (e) {
    console.error('Error detecting Safari version:', e)
    return false
  }
}

// Function to initialize PDF worker
function initPDFWorker() {
  try {
    if (typeof window !== 'undefined') {
      const useLegacy = shouldUseLegacyBuild()
      // Use local worker file instead of unpkg
      const workerSrc = useLegacy
        ? new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).href
        : new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href
      console.log('Setting PDF worker to:', workerSrc)
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc
      pdfjs.GlobalWorkerOptions.workerPort = null
    }
  } catch (e) {
    console.error('Error setting PDF worker:', e)
  }
}

// Initialize the worker at root
initPDFWorker()

import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import { getTeamComponentFromPath, isSafari, ua } from '@acre/utils'
import { LutherException } from '@acre/graphql'
import '@acre/design-system/mui-x-license'
import acreConfig from '@acre/config'

import App from './App'
import AppProviders from './AppProviders'

const isPlaywright = navigator.webdriver
// @ts-expect-error - Cypress is a global variable
const isCypress = window.Cypress

// Disable sentry reporting in test environments
if (import.meta.env.MODE === 'production' && !isCypress && !isPlaywright) {
  init({
    dsn: 'https://ee7d1ac2845d4d9a998f53da3575f68a@sentry.io/1876005',
    environment: acreConfig.SENTRY_ENVIRONMENT,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new ExtraErrorData({ depth: 5 }),
    ],
    tracesSampler: (samplingContext) => {
      if (samplingContext?.transactionContext?.op === 'navigation') return 0.015 // 1.5%
      // Apply a 0.15% sample rate for all other transactions (10× lower)
      return 0.0015
    },
    beforeSend(event, hint) {
      // This ensures that sentry is grouping our events as we want them, rather than putting all failing API calls together
      const error = hint?.originalException as Error

      // Ignore ApolloErrors
      if (error?.name === 'ApolloError') return null

      const hasGraphqlError =
        event.breadcrumbs?.some(
          (b) => b.category === 'sentry.event' && b.message && /GraphqlError:/i.test(b.message),
        ) || false

      // Skip logging GraphQL errors here, as they're already handled by graphqlErrorLink.
      if (hasGraphqlError) return null

      if (error && typeof error !== 'string' && error.name === 'LutherException') {
        const err = error as LutherException

        event.fingerprint = [err.message]
      }

      // Deducing the team and component from the URL and adding them to the tags list
      const teamTags = getTeamComponentFromPath(window.location.pathname, 'crm')
      event.tags = {
        ...event.tags,
        team: teamTags.team,
        component: teamTags.component,
      }

      return event
    },
  })

  setTag('issue_type', 'frontend')
  setTag('app', 'broker-crm')
}

// Allows us to use the sentry profiler, can track render times for the app component and descendants
const AppWithProfiler = withProfiler(App)

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <AppProviders>
      <AppWithProfiler />
    </AppProviders>
  </StrictMode>,
)
